<template>
  <div class="h-full flex flex-col overflow-hidden p-3">
    <h2 class="mb-3">User Management</h2>

    <ProgressSpinner v-if="loading" />

    <div v-else class="flex flex-col flex-1 min-h-0">
      <!-- Bulk Actions Section -->
      <div class="mb-4 flex flex-wrap gap-2 items-center">
        <!-- Mindset360 Dropdown Menu -->
        <SplitButton
          label="External Evaluation Actions"
          icon="pi pi-cog"
          class="p-button-sm"
          :disabled="loading"
          :model="externalEvaluationMenuItems"
        />

        <!-- DevTip Actions -->
        <Button
          label="Send DevTip"
          icon="pi pi-send"
          class="p-button-sm p-button-info"
          :disabled="!selectedUsers || selectedUsers.length === 0 || loading"
          @click="showDevTipDialog = true"
        />

        <!-- Generate Random Data -->
        <Button
          label="Generate Random Data"
          icon="pi pi-database"
          class="p-button-sm p-button-warning"
          :disabled="!selectedUsers || selectedUsers.length === 0 || loading"
          @click="showRandomDataDialog = true"
        />

        <!-- Generate KSS Test Data -->
        <Button
          label="Generate KSS Test Data"
          icon="pi pi-chart-bar"
          class="p-button-sm p-button-warning"
          :disabled="!selectedUsers || selectedUsers.length === 0 || loading"
          @click="showKssTestDataDialog = true"
        />

        <!-- Remove KSS Test Data -->
        <Button
          label="Remove KSS Test Data"
          icon="pi pi-trash"
          class="p-button-sm p-button-danger"
          :disabled="loading || removingKssTestData"
          @click="confirmRemoveKssTestData"
        />

        <Button
          label="Export permissions CSV"
          icon="pi pi-download"
          class="p-button-sm p-button-secondary"
          :disabled="loading"
          @click="exportPermissionsCsv"
        />

        <!-- Selection Counter -->
        <span v-if="selectedUsers && selectedUsers.length > 0" class="text-sm text-gray-600">
          {{ selectedUsers.length }} user(s) selected
        </span>
      </div>

      <!-- Filter Bar -->
      <div class="flex flex-wrap gap-2 items-end mb-4 p-3 surface-ground border-round">
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-gray-500">Search</label>
          <InputText v-model="filters.search" placeholder="Name or email..." style="width: 220px" />
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-gray-500">Status</label>
          <Dropdown v-model="filters.userStatus" :options="userStatusOptions" optionLabel="label" optionValue="value"
            placeholder="All" style="width: 140px" />
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-gray-500">Roles</label>
          <MultiSelect v-model="filters.roles" :options="roleFilterOptions" optionLabel="value" optionValue="value"
            placeholder="All roles" :maxSelectedLabels="1" selectedItemsLabel="{0} roles"
            style="width: 180px" />
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-gray-500">Travel Buddy</label>
          <Dropdown v-model="filters.travelBuddy" :options="booleanFilterOptions" optionLabel="label" optionValue="value"
            placeholder="All" style="width: 140px" />
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-gray-500">Reflection GL</label>
          <Dropdown v-model="filters.reflexionGuideline" :options="booleanFilterOptions" optionLabel="label" optionValue="value"
            placeholder="All" style="width: 140px" />
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-gray-500">External Eval</label>
          <Dropdown v-model="filters.externalEvaluation" :options="enabledFilterOptions" optionLabel="label" optionValue="value"
            placeholder="All" style="width: 140px" />
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-gray-500">Valid</label>
          <Dropdown v-model="filters.isValid" :options="validFilterOptions" optionLabel="label" optionValue="value"
            placeholder="All" style="width: 100px" />
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-gray-500">&nbsp;</label>
          <Button icon="pi pi-filter-slash" severity="secondary" text rounded
            :disabled="!hasActiveFilters" @click="clearFilters" title="Clear all filters" />
        </div>
      </div>

      <div v-if="hasActiveFilters" class="text-sm text-gray-500 mb-2">
        Showing {{ filteredUsers.length }} of {{ (users || []).length }} users
      </div>

      <!-- Users Table -->
      <div class="flex-1 min-h-0">
      <DataTable
        :value="filteredUsers"
        dataKey="_id"
        v-model:selection="selectedUsers"
        :paginator="true"
        :rows="20"
        :rowsPerPageOptions="[10, 20, 50, 100]"
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks
         NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} users"
        :lazy="false"
        :scrollable="true"
        scrollHeight="flex"
      >
      <Column selectionMode="multiple" headerStyle="width: 3rem"></Column>
      <Column field="name" header="Username" :sortable="true">
        <template #body="{ data }">
          <span class="font-medium">{{ data.name }}</span>
        </template>
      </Column>
      <Column field="email" header="Email" :sortable="true" style="min-width: 14rem">
        <template #body="{ data }">
          <span>{{ data.email }}</span>
        </template>
      </Column>
            <Column field="roles" header="Roles">
        <template #body="slotProps">
          <MultiSelect
            :key="rolesMultiSelectKey(slotProps.data._id)"
            :modelValue="slotProps.data.roles"
            :options="roleSelectOptionsForUser(slotProps.data)"
            optionLabel="label"
            optionValue="value"
            optionDisabled="disabled"
            class="w-full"
            filter
            :selectAllLabel="'Select All Roles'"
            :placeholder="'Select roles...'"
            @update:modelValue="(value) => onRolesChange(slotProps.data, value)"
          />
        </template>
      </Column>
      <Column field="reflexionGuidelineActivated" header="Reflection Guidelines">
        <template #body="slotProps">
          <Checkbox
            :modelValue="slotProps.data.reflexionGuidelineActivated || false"
            :binary="true"
            @update:modelValue="(value) => onReflexionGuidelineChange(slotProps.data._id, value)"
          />
        </template>
      </Column>
        <Column field="externalEvaluationEnabled" header="External Evaluation (Fremdbild) Enabled">
          <template #body="slotProps">
            <Checkbox
              :modelValue="slotProps.data.externalEvaluationEnabled"
              @update:modelValue="onExternalEvaluationToggle(slotProps.data._id, $event)"
              :disabled="loading"
              binary
            />
          </template>
        </Column>
      <Column field="travelBuddyActivated" header="Travel Buddy">
        <template #body="slotProps">
          <Checkbox
            :modelValue="slotProps.data.travelBuddySystem?.activated || false"
            :binary="true"
            :disabled="loading"
            @update:modelValue="(value) => onTravelBuddyActivatedChange(slotProps.data._id, value)"
          />
        </template>
      </Column>
      <Column field="userStatus" header="User Status">
        <template #body="slotProps">
          <span>{{ slotProps.data.userStatus }}</span>
        </template>
      </Column>
      <Column field="isValid" header="Is Valid ?">
        <template #body="slotProps">
          <span>{{ slotProps.data.isValid ? 'Yes' : 'No' }}</span>
        </template>
      </Column>
      <Column header="Actions" style="width: 80px" :exportable="false">
        <template #body="slotProps">
          <Button icon="pi pi-eye" class="p-button-sm p-button-text p-button-rounded"
            @click="openUserOverview(slotProps.data)" title="View Details" />
        </template>
      </Column>
      </DataTable>
      </div>

      <!-- User Overview Dialog -->
      <UserOverviewDialog
        v-model:visible="showUserOverview"
        :user="selectedOverviewUser"
      />

      <!-- DevTip Dialog -->
      <Dialog
        v-model:visible="showDevTipDialog"
        header="Send DevTip to Users"
        :modal="true"
        :closable="true"
        :style="{ width: '50vw' }"
        :maximizable="true"
      >
        <div class="p-fluid">
          <div class="field">
            <label for="schemaSelect">Select Schema:</label>
            <Dropdown
              id="schemaSelect"
              v-model="selectedSchema"
              :options="availableSchemas"
              optionLabel="name"
              optionValue="id"
              placeholder="Choose a schema"
              :loading="loadingSchemas"
              class="w-full"
            />
          </div>

          <div class="field">
            <label>Selected Users ({{ selectedUsers?.length || 0 }}):</label>
            <div class="p-2 border-round" style="max-height: 200px; overflow-y: auto;">
              <div v-for="user in selectedUsers" :key="user._id" class="p-1">
                {{ user.name }} ({{ user.email }})
              </div>
            </div>
          </div>
        </div>

        <template #footer>
          <Button
            label="Cancel"
            icon="pi pi-times"
            class="p-button-text"
            @click="showDevTipDialog = false"
          />
          <Button
            label="Send DevTip"
            icon="pi pi-send"
            class="p-button-info"
            :disabled="!selectedSchema || sendingDevTip"
            :loading="sendingDevTip"
            @click="sendDevTipToUsers"
          />
        </template>
      </Dialog>

      <!-- Generate KSS Test Data Dialog -->
      <Dialog
        v-model:visible="showKssTestDataDialog"
        header="Generate KSS Test Data"
        :modal="true"
        :closable="true"
        :style="{ width: '50vw' }"
      >
        <div class="p-fluid flex flex-col gap-4">
          <div class="flex items-center gap-2 p-3 border-round"
            style="background-color: var(--yellow-50); border: 1px solid var(--yellow-200);">
            <i class="pi pi-info-circle" style="color: var(--yellow-700); font-size: 1.5rem;"></i>
            <span style="color: var(--yellow-900);">
              This will generate <strong>KSS assessment data</strong> for the selected users
              to test the KSS trend analysis dashboard.
            </span>
          </div>

          <div class="field">
            <label for="kssName">Meeting Name <span class="text-red-500">*</span></label>
            <InputText
              id="kssName"
              v-model="kssTestDataName"
              :placeholder="kssTestDataRecurring ? 'e.g. Weekly Sync Q1' : 'e.g. Retro Sprint 12'"
            />
          </div>

          <div class="field">
            <label class="flex items-center gap-2 cursor-pointer">
              <Checkbox
                v-model="kssTestDataRecurring"
                :binary="true"
              />
              <span>Recurring series (multiple occurrences)</span>
            </label>
          </div>

          <div v-if="kssTestDataRecurring" class="flex gap-4">
            <div class="field flex-1">
              <label for="kssOccurrences">Occurrences</label>
              <InputNumber
                id="kssOccurrences"
                v-model="kssTestDataOccurrences"
                :min="2"
                :max="12"
                showButtons
              />
            </div>
            <div class="field flex-1">
              <label for="kssFrequency">Frequency</label>
              <Dropdown
                id="kssFrequency"
                v-model="kssTestDataFrequency"
                :options="kssFrequencyOptions"
                optionLabel="label"
                optionValue="value"
              />
            </div>
          </div>

          <div class="field">
            <label for="kssRespondents">
              Respondents (out of {{ selectedUsers?.length || 0 }} selected users)
            </label>
            <InputNumber
              id="kssRespondents"
              v-model="kssTestDataRespondents"
              :min="1"
              :max="selectedUsers?.length || 1"
              showButtons
            />
          </div>

          <div class="flex items-center gap-2 p-2 border-round"
            style="background-color: var(--blue-50); border: 1px solid var(--blue-200);">
            <i class="pi pi-chart-bar" style="color: var(--blue-700);"></i>
            <span style="color: var(--blue-900);" class="text-sm">
              <strong>{{ kssTestDataRecurring ? 'Recurring' : 'Single meeting' }}</strong> &mdash;
              Will create
              <template v-if="kssTestDataRecurring">
                <strong>{{ kssTestDataOccurrences }}</strong> {{ kssTestDataFrequency }}
                occurrence(s) &times;
              </template>
              <strong>{{ selectedUsers?.length || 0 }}</strong> users
              = <strong>{{ kssComputedTotalAssessments }}</strong> assessments
              (<strong>{{ kssComputedSubmittedCount }}</strong> submitted,
              <strong>{{ kssComputedOpenCount }}</strong> open).
            </span>
          </div>
        </div>

        <template #footer>
          <Button
            label="Cancel"
            icon="pi pi-times"
            class="p-button-text"
            @click="showKssTestDataDialog = false"
          />
          <Button
            label="Generate"
            icon="pi pi-check"
            class="p-button-warning"
            :disabled="!kssTestDataName || generatingKssTestData"
            :loading="generatingKssTestData"
            @click="executeGenerateKssTestData"
          />
        </template>
      </Dialog>

      <!-- Generate Random Data Dialog -->
      <Dialog
        v-model:visible="showRandomDataDialog"
        header="Generate Random Assessment Data"
        :modal="true"
        :closable="true"
        :style="{ width: '55vw' }"
        :maximizable="true"
      >
        <div class="p-fluid">
          <!-- Warning Banner -->
          <div class="flex items-center gap-2 p-3 mb-3 border-round"
            style="background-color: var(--yellow-50); border: 1px solid var(--yellow-200);">
            <i class="pi pi-exclamation-triangle" style="color: var(--yellow-700); font-size: 1.5rem;"></i>
            <span style="color: var(--yellow-900);">
              This will generate <strong>FAKE</strong> assessment data for the selected users.
              This action cannot be easily undone.
            </span>
          </div>

          <!-- Schema Selection -->
          <div class="field">
            <label for="randomDataSchemaSelect">Select Assessment Schema:</label>
            <Dropdown
              id="randomDataSchemaSelect"
              v-model="selectedRandomDataSchema"
              :options="allSchemas"
              optionLabel="name"
              optionValue="id"
              placeholder="Choose a schema"
              :loading="loadingSchemas"
              class="w-full"
              @change="onRandomDataSchemaSelected"
            />
          </div>

          <!-- Chart Type Info -->
          <div v-if="randomDataChartType" class="field">
            <div class="flex items-center gap-2 p-2 border-round"
              style="background-color: var(--blue-50); border: 1px solid var(--blue-200);">
              <i class="pi pi-chart-bar" style="color: var(--blue-700);"></i>
              <span style="color: var(--blue-900);">
                <strong>Chart type: {{ randomDataChartType }}</strong> &mdash;
                <span v-if="randomDataChartType === 'line'">
                  Will generate 7 daily assessments for the past week.
                </span>
                <span v-else>
                  Will generate 1 assessment with today's date.
                </span>
              </span>
            </div>
          </div>

          <!-- Selected Users List -->
          <div class="field">
            <label>Selected Users ({{ selectedUsers?.length || 0 }}):</label>
            <div class="p-2 border-round surface-ground" style="max-height: 200px; overflow-y: auto;">
              <div v-for="user in selectedUsers" :key="user._id" class="p-1">
                {{ user.name }} ({{ user.email }})
              </div>
            </div>
          </div>
        </div>

        <template #footer>
          <Button
            label="Cancel"
            icon="pi pi-times"
            class="p-button-text"
            @click="showRandomDataDialog = false"
          />
          <Button
            label="Generate Data"
            icon="pi pi-exclamation-triangle"
            class="p-button-danger"
            :disabled="!selectedRandomDataSchema || generatingRandomData"
            :loading="generatingRandomData"
            @click="executeGenerateRandomData"
          />
        </template>
      </Dialog>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import usersService from '../../../services/usersService';
import { USER_ROLES_ENUM_NAME, TENANT_ROLE_ADMIN } from '../../../services/enumService';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import MultiSelect from 'primevue/multiselect';
import ProgressSpinner from 'primevue/progressspinner';
import Checkbox from 'primevue/checkbox';
import SplitButton from 'primevue/splitbutton';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import Dropdown from 'primevue/dropdown';
import InputNumber from 'primevue/inputnumber';
import UserOverviewDialog from './UserOverviewDialog.vue';

export default {
  name: 'UsersManager',
  components: {
    DataTable,
    Column,
    MultiSelect,
    ProgressSpinner,
    Checkbox,
    SplitButton,
    InputText,
    Button,
    Dialog,
    Dropdown,
    InputNumber,
    UserOverviewDialog,
  },
  data() {
    return {
      selectedUsers: null,
      showDevTipDialog: false,
      selectedSchema: null,
      availableSchemas: [],
      loadingSchemas: false,
      sendingDevTip: false,
      showUserOverview: false,
      selectedOverviewUser: null,
      showRandomDataDialog: false,
      selectedRandomDataSchema: null,
      generatingRandomData: false,
      randomDataChartType: null,
      allSchemas: [],
      showKssTestDataDialog: false,
      kssTestDataName: '',
      kssTestDataRecurring: true,
      kssTestDataOccurrences: 5,
      kssTestDataFrequency: 'weekly',
      kssTestDataRespondents: null,
      generatingKssTestData: false,
      removingKssTestData: false,
      kssFrequencyOptions: [
        { label: 'Daily', value: 'daily' },
        { label: 'Weekly', value: 'weekly' },
        { label: 'Monthly', value: 'monthly' },
      ],
      filters: {
        search: '',
        userStatus: 'active',
        roles: [],
        travelBuddy: null,
        reflexionGuideline: null,
        externalEvaluation: null,
        isValid: null,
      },
      booleanFilterOptions: [
        { label: 'All', value: null },
        { label: 'Activated', value: true },
        { label: 'Deactivated', value: false },
      ],
      enabledFilterOptions: [
        { label: 'All', value: null },
        { label: 'Enabled', value: true },
        { label: 'Disabled', value: false },
      ],
      validFilterOptions: [
        { label: 'All', value: null },
        { label: 'Yes', value: true },
        { label: 'No', value: false },
      ],
      /** Bump to remount a row’s roles MultiSelect after a rejected change (PrimeVue sync). */
      rolesMultiSelectEpoch: {},
      externalEvaluationMenuItems: [
        {
          label: 'Enable All External Evaluation',
          icon: 'pi pi-check',
          command: () => this.enableAllUsers(),
          disabled: false,
        },
        {
          label: 'Disable All External Evaluation',
          icon: 'pi pi-times',
          command: () => this.disableAllUsers(),
          disabled: false,
        },
        {
          separator: true,
        },
        {
          label: 'Enable Selected External Evaluation',
          icon: 'pi pi-check',
          command: () => this.enableSelectedUsers(),
          disabled: true,
        },
        {
          label: 'Disable Selected External Evaluation',
          icon: 'pi pi-times',
          command: () => this.disableSelectedUsers(),
          disabled: true,
        },
        {
          separator: true,
        },
        {
          label: 'Generate External Evaluation Test Data',
          icon: 'pi pi-database',
          command: () => this.generateTestData(),
          disabled: false,
        },
      ],
    };
  },
  computed: {
    users() {
      return this.$store.state.userManagement.users;
    },
    loading() {
      return this.$store.state.userManagement.loading;
    },
    ...mapGetters('enums', ['getEnumTypeByName', 'getActiveEnumValues']),

    /** Active role values only (filters + baseline options) */
    roleFilterOptions() {
      return this.getActiveEnumValues(USER_ROLES_ENUM_NAME) || [];
    },

    /** Active `user-roles` as MultiSelect `{ label, value, disabled }` (shared by all rows). */
    activeUserRoleSelectOptions() {
      const enums = this.getEnumTypeByName(USER_ROLES_ENUM_NAME);
      return (enums?.values || [])
        .filter((v) => v.isActive)
        .map((v) => ({
          label: v.value,
          value: v.value,
          disabled: false,
        }));
    },
    kssEffectiveOccurrences() {
      return this.kssTestDataRecurring ? this.kssTestDataOccurrences : 1;
    },
    kssComputedTotalAssessments() {
      return this.kssEffectiveOccurrences * (this.selectedUsers?.length || 0);
    },
    kssComputedSubmittedCount() {
      return this.kssEffectiveOccurrences * (this.kssTestDataRespondents || 0);
    },
    kssComputedOpenCount() {
      return this.kssComputedTotalAssessments - this.kssComputedSubmittedCount;
    },
    userStatusOptions() {
      const statuses = new Set((this.users || []).map((u) => u.userStatus).filter(Boolean));
      return [{ label: 'All', value: null }, ...[...statuses].sort().map((s) => ({ label: s, value: s }))];
    },
    filteredUsers() {
      let result = this.users || [];
      if (this.filters.search) {
        const q = this.filters.search.toLowerCase();
        result = result.filter((u) =>
          (u.name || '').toLowerCase().includes(q)
          || (u.email || '').toLowerCase().includes(q),
        );
      }
      if (this.filters.userStatus) {
        result = result.filter((u) => u.userStatus === this.filters.userStatus);
      }
      if (this.filters.roles && this.filters.roles.length > 0) {
        result = result.filter((u) =>
          (u.roles || []).some((r) => this.filters.roles.includes(r)),
        );
      }
      if (this.filters.travelBuddy != null) {
        result = result.filter((u) =>
          (u.travelBuddySystem?.activated || false) === this.filters.travelBuddy,
        );
      }
      if (this.filters.reflexionGuideline != null) {
        result = result.filter((u) =>
          (u.reflexionGuidelineActivated || false) === this.filters.reflexionGuideline,
        );
      }
      if (this.filters.externalEvaluation != null) {
        result = result.filter((u) =>
          (u.externalEvaluationEnabled || false) === this.filters.externalEvaluation,
        );
      }
      if (this.filters.isValid != null) {
        result = result.filter((u) => (u.isValid || false) === this.filters.isValid);
      }
      return result;
    },
    hasActiveFilters() {
      return this.filters.search
        || this.filters.userStatus != null
        || (this.filters.roles && this.filters.roles.length > 0)
        || this.filters.travelBuddy != null
        || this.filters.reflexionGuideline != null
        || this.filters.externalEvaluation != null
        || this.filters.isValid != null;
    },
  },
  watch: {
    loading() {
      this.updateMenuDisabledStates();
    },
    selectedUsers() {
      this.updateMenuDisabledStates();
    },
    showDevTipDialog(newVal) {
      if (newVal) {
        this.loadAvailableSchemas();
      }
    },
    showRandomDataDialog(newVal) {
      if (newVal) {
        this.loadAllSchemas();
        this.selectedRandomDataSchema = null;
        this.randomDataChartType = null;
      }
    },
    showKssTestDataDialog(newVal) {
      if (newVal) {
        this.kssTestDataName = '';
        this.kssTestDataRecurring = true;
        this.kssTestDataOccurrences = 5;
        this.kssTestDataFrequency = 'weekly';
        this.kssTestDataRespondents = this.selectedUsers?.length || 1;
      }
    },
  },
  methods: {
    ...mapActions('enums', ['fetchEnumTypes']),

    openUserOverview(user) {
      this.selectedOverviewUser = user;
      this.showUserOverview = true;
    },

    clearFilters() {
      this.filters = {
        search: '',
        userStatus: 'active',
        roles: [],
        travelBuddy: null,
        reflexionGuideline: null,
        externalEvaluation: null,
        isValid: null,
      };
    },

    updateMenuDisabledStates() {
      const hasSelectedUsers = this.selectedUsers && this.selectedUsers.length > 0;
      // Update only the disabled states
      this.externalEvaluationMenuItems[0].disabled = this.loading; // Enable All
      this.externalEvaluationMenuItems[1].disabled = this.loading; // Disable All
      this.externalEvaluationMenuItems[3].disabled =
        this.loading || !hasSelectedUsers; // Enable Selected
      this.externalEvaluationMenuItems[4].disabled =
        this.loading || !hasSelectedUsers; // Disable Selected
      this.externalEvaluationMenuItems[6].disabled = this.loading; // Generate Test Data
    },

    /**
     * Other active tenant users who currently have admin (matches backend last-admin rule).
     */
    otherActiveAdminsCount(excludeUserId) {
      return (this.users || []).filter(
        (u) =>
          String(u._id) !== String(excludeUserId)
          && u.userStatus === 'active'
          && Array.isArray(u.roles)
          && u.roles.includes(TENANT_ROLE_ADMIN),
      ).length;
    },

    /** True if applying newRoles would remove admin while this user is the only active admin. */
    wouldStripLastActiveAdmin(user, newRoles) {
      const had = (user?.roles || []).includes(TENANT_ROLE_ADMIN);
      const will = (newRoles || []).includes(TENANT_ROLE_ADMIN);
      if (!had || will) return false;
      return this.otherActiveAdminsCount(user._id) === 0;
    },

    rolesMultiSelectKey(userId) {
      const n = this.rolesMultiSelectEpoch[userId] || 0;
      return `roles-ms-${userId}-${n}`;
    },

    bumpRolesMultiSelectEpoch(userId) {
      this.rolesMultiSelectEpoch = {
        ...this.rolesMultiSelectEpoch,
        [userId]: (this.rolesMultiSelectEpoch[userId] || 0) + 1,
      };
    },

    roleSelectOptionsForUser(user) {
      const lockAdmin =
        (user?.roles || []).includes(TENANT_ROLE_ADMIN)
        && this.otherActiveAdminsCount(user._id) === 0;
      const activeVals = this.activeUserRoleSelectOptions.map((o) => ({
        ...o,
        disabled: o.value === TENANT_ROLE_ADMIN && lockAdmin,
      }));
      const seen = new Set(activeVals.map((o) => o.value));
      const extra = [];
      for (const r of user?.roles || []) {
        if (!seen.has(r)) {
          seen.add(r);
          extra.push({
            label: `${r} (inactive)`,
            value: r,
            disabled: true,
          });
        }
      }
      return [...activeVals, ...extra];
    },

    async exportPermissionsCsv() {
      try {
        const res = await usersService.exportPermissionsCsv();
        const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'user-permissions.csv';
        a.click();
        window.URL.revokeObjectURL(url);
        this.$store.dispatch(
          'notifications/showToast',
          { detail: 'Export downloaded', severity: 'success' },
          { root: true },
        );
      } catch (err) {
        console.error(err);
        this.$store.dispatch(
          'notifications/showToast',
          { detail: 'Export failed', severity: 'error' },
          { root: true },
        );
      }
    },

    async init() {
      try {
        await this.fetchEnumTypes();
        await this.$store.dispatch('loadUsers');
      } catch (err) {
        console.error('Failed to initialize user manager:', err);
        this.$store.dispatch(
          'notifications/showToast',
          {
            detail: 'Failed to initialize user manager',
            severity: 'error',
          },
          { root: true },
        );
      }
    },

    async onRolesChange(user, newRoles) {
      const userId = user._id;
      if (this.wouldStripLastActiveAdmin(user, newRoles)) {
        this.bumpRolesMultiSelectEpoch(userId);
        await this.$store.dispatch(
          'notifications/showToast',
          {
            detail:
              'Cannot remove the last tenant admin. Give another active user the admin role first.',
            severity: 'warn',
          },
          { root: true },
        );
        return;
      }
      try {
        await this.$store.dispatch('updateUser', { userId, roles: newRoles });
      } catch (error) {
        console.error('Failed to update user roles:', error);
        this.bumpRolesMultiSelectEpoch(userId);
      }
    },

    async onReflexionGuidelineChange(userId, isActivated) {
      try {
        await this.$store.dispatch('updateUserReflexionGuideline', {
          userId,
          reflexionGuidelineActivated: isActivated,
        });
      } catch (error) {
        console.error('Failed to update reflection guideline status:', error);
        this.$store.dispatch('notifications/showToast', {
          detail: 'Failed to update reflection guideline status',
          severity: 'error',
        }, { root: true });
      }
    },

    async onTravelBuddyActivatedChange(userId, value) {
      try {
        await this.$store.dispatch('updateUserTravelBuddyActivated', {
          userId,
          travelBuddyActivated: value,
        });
      } catch (error) {
        console.error('Failed to update travel buddy status:', error);
        this.$store.dispatch('notifications/showToast', {
          detail: 'Failed to update travel buddy status',
          severity: 'error',
        }, { root: true });
      }
    },

    async onExternalEvaluationToggle(userId, value) {
      try {
        // Use the store action for individual toggle
        await this.$store.dispatch('setExternalEvaluationEnabled', { userIds: [userId], setTo: value });

        // Show success notification
        this.$store.dispatch('notifications/showToast', {
          detail: 'External Evaluation setting updated successfully',
          severity: 'success',
        }, { root: true });
      } catch (error) {
        console.error('Failed to toggle external evaluation for user:', error);
        this.$store.dispatch('notifications/showToast', {
          detail: 'Failed to update External Evaluation setting',
          severity: 'error',
        }, { root: true });
      }
    },

    async enableAllUsers() {
      try {
        this.$store.commit('setLoading', true);
        await this.$store.dispatch('setExternalEvaluationEnabled', { userIds: [], setTo: true });
        await this.$store.dispatch('loadUsers'); // Reload to show updated state
        this.$store.dispatch('notifications/showToast', {
          detail: 'Successfully enabled external evaluation for all users',
          severity: 'success',
        }, { root: true });
      } catch (error) {
        console.error('Failed to enable external evaluation for all users:', error);
        this.$store.dispatch('notifications/showToast', {
          detail: 'Failed to enable external evaluation for all users',
          severity: 'error',
        }, { root: true });
      } finally {
        this.$store.commit('setLoading', false);
      }
    },

    async disableAllUsers() {
      try {
        this.$store.commit('setLoading', true);
        await this.$store.dispatch('setExternalEvaluationEnabled', { userIds: [], setTo: false });
        await this.$store.dispatch('loadUsers'); // Reload to show updated state
        this.$store.dispatch('notifications/showToast', {
          detail: 'Successfully disabled external evaluation for all users',
          severity: 'success',
        }, { root: true });
      } catch (error) {
        console.error('Failed to disable external evaluation for all users:', error);
        this.$store.dispatch('notifications/showToast', {
          detail: 'Failed to disable external evaluation for all users',
          severity: 'error',
        }, { root: true });
      } finally {
        this.$store.commit('setLoading', false);
      }
    },

    async enableSelectedUsers() {
      if (!this.selectedUsers || this.selectedUsers.length === 0) return;
      try {
        this.$store.commit('setLoading', true);
        const userIds = this.selectedUsers.map((user) => user._id);
        await this.$store.dispatch('setExternalEvaluationEnabled', { userIds, setTo: true });
        this.selectedUsers = null; // Clear selection after action
        await this.$store.dispatch('loadUsers'); // Reload to show updated state
        this.$store.dispatch('notifications/showToast', {
          detail: `Successfully enabled external evaluation for ${userIds.length} selected users`,
          severity: 'success',
        }, { root: true });
      } catch (error) {
        console.error('Failed to enable external evaluation for selected users:', error);
        this.$store.dispatch('notifications/showToast', {
          detail: 'Failed to enable external evaluation for selected users',
          severity: 'error',
        }, { root: true });
      } finally {
        this.$store.commit('setLoading', false);
      }
    },

    async disableSelectedUsers() {
      if (!this.selectedUsers || this.selectedUsers.length === 0) return;
      try {
        this.$store.commit('setLoading', true);
        const userIds = this.selectedUsers.map((user) => user._id);
        await this.$store.dispatch('setExternalEvaluationEnabled', { userIds, setTo: false });
        this.selectedUsers = null; // Clear selection after action
        await this.$store.dispatch('loadUsers'); // Reload to show updated state
        this.$store.dispatch('notifications/showToast', {
          detail: `Successfully disabled external evaluation for ${userIds.length} selected users`,
          severity: 'success',
        }, { root: true });
      } catch (error) {
        console.error('Failed to disable external evaluation for selected users:', error);
        this.$store.dispatch('notifications/showToast', {
          detail: 'Failed to disable external evaluation for selected users',
          severity: 'error',
        }, { root: true });
      } finally {
        this.$store.commit('setLoading', false);
      }
    },

    async generateTestData() {
      try {
        // Check if users are selected
        if (!this.selectedUsers || this.selectedUsers.length === 0) {
          this.$store.dispatch('notifications/showToast', {
            detail: 'Please select users first',
            severity: 'warn',
          }, { root: true });
          return;
        }

        // Show confirmation dialog
        this.$confirm.require({
          message: `This will generate external evaluation test assessment data for ${this.selectedUsers.length} selected user(s). Are you sure you want to continue?`,
          header: 'Generate External Evaluation Test Data',
          icon: 'pi pi-exclamation-triangle',
          acceptLabel: 'Yes, Generate',
          rejectLabel: 'Cancel',
          accept: async () => {
            await this.executeGenerateTestData();
          },
        });
      } catch (error) {
        console.error('Failed to show confirmation dialog:', error);
        this.$store.dispatch('notifications/showToast', {
          detail: 'Failed to show confirmation dialog',
          severity: 'error',
        }, { root: true });
      }
    },

    async executeGenerateTestData() {
      try {
        // Set loading state
        this.loading = true;

        let successCount = 0;
        let errorCount = 0;
        const results = [];

        // Process users in smaller batches to avoid overwhelming the server
        const batchSize = 3;
        const userBatches = [];
        for (let i = 0; i < this.selectedUsers.length; i += batchSize) {
          userBatches.push(this.selectedUsers.slice(i, i + batchSize));
        }

        // Process all batches sequentially using reduce
        await userBatches.reduce(async (previousPromise, batch, batchIndex) => {
          await previousPromise;

          const batchPromises = batch.map(async (user) => {
            try {
              const result = await this.$store.dispatch('generateTestData', user._id);
              return { user, result, success: result.success };
            } catch (error) {
              return { user, result: { message: error.message }, success: false };
            }
          });

          const batchResults = await Promise.all(batchPromises);
          results.push(...batchResults);

          // Update counters
          batchResults.forEach(({ success }) => {
            if (success) {
              successCount += 1;
            } else {
              errorCount += 1;
            }
          });

          // Small delay between batches to prevent server overload
          if (batchIndex < userBatches.length - 1) {
            await new Promise((resolve) => {
              setTimeout(() => {
                resolve();
              }, 100);
            });
          }
        }, Promise.resolve());

        // Clear selection after processing
        this.selectedUsers = null;

        // Only reload users if we had successful generations
        if (successCount > 0) {
          await this.$store.dispatch('loadUsers');
        }

        // Show summary notification
        if (successCount > 0 && errorCount === 0) {
          this.$store.dispatch('notifications/showToast', {
            detail: `Successfully generated external evaluation test data for ${successCount} user(s)`,
            severity: 'success',
          }, { root: true });
        } else if (successCount > 0 && errorCount > 0) {
          this.$store.dispatch('notifications/showToast', {
            detail: `Generated test data for ${successCount} user(s), failed for ${errorCount} user(s)`,
            severity: 'warn',
          }, { root: true });
        } else {
          this.$store.dispatch('notifications/showToast', {
            detail: 'Failed to generate test data for all selected users',
            severity: 'error',
          }, { root: true });
        }
      } catch (error) {
        console.error('Failed to generate test data:', error);
        this.$store.dispatch('notifications/showToast', {
          detail: error.message || 'Failed to generate test data',
          severity: 'error',
        }, { root: true });
      } finally {
        // Always clear loading state
        this.loading = false;
      }
    },

    // ===== DEVTIP METHODS =====

    async loadAvailableSchemas() {
      try {
        this.loadingSchemas = true;
        // Use schemas from store - they're already loaded!
        const schemas = this.$store.getters['assessmentSchemas/assessmentSchemas'] || [];
        this.availableSchemas = schemas
          .filter((schema) => schema?.config?.devTips?.enabled === true)
          .map((schema) => ({
            id: schema._id,
            name: schema.representationProperties?.assessmentName?.en || schema.type || 'Unknown Schema',
            type: schema.type,
          }));
      } catch (error) {
        console.error('Failed to load schemas:', error);
        this.$store.dispatch('notifications/showToast', {
          detail: 'Failed to load available schemas',
          severity: 'error',
        }, { root: true });
      } finally {
        this.loadingSchemas = false;
      }
    },

    // ===== GENERATE RANDOM DATA METHODS =====

    async loadAllSchemas() {
      try {
        this.loadingSchemas = true;
        const schemas = this.$store.getters['assessmentSchemas/assessmentSchemas'] || [];
        this.allSchemas = schemas.map((schema) => ({
          id: schema._id,
          name: schema.representationProperties?.assessmentName?.en || schema.type || 'Unknown Schema',
          type: schema.type,
        }));
      } catch (error) {
        console.error('Failed to load schemas:', error);
        this.$store.dispatch('notifications/showToast', {
          detail: 'Failed to load available schemas',
          severity: 'error',
        }, { root: true });
      } finally {
        this.loadingSchemas = false;
      }
    },

    async onRandomDataSchemaSelected() {
      if (!this.selectedRandomDataSchema) {
        this.randomDataChartType = null;
        return;
      }

      try {
        const charts = this.$store.getters['charts/getChartByAssessmentId'](this.selectedRandomDataSchema);
        if (charts && charts.length > 0) {
          this.randomDataChartType = charts[0].type;
        } else {
          await this.$store.dispatch('charts/fetchChartByAssessmentId', this.selectedRandomDataSchema);
          const updatedCharts = this.$store.getters['charts/getChartByAssessmentId'](this.selectedRandomDataSchema);
          this.randomDataChartType = updatedCharts && updatedCharts.length > 0
            ? updatedCharts[0].type
            : 'bar';
        }
      } catch (error) {
        console.error('Failed to fetch chart type:', error);
        this.randomDataChartType = 'bar';
      }
    },

    async executeGenerateRandomData() {
      if (!this.selectedRandomDataSchema || !this.selectedUsers || this.selectedUsers.length === 0) {
        return;
      }

      try {
        this.generatingRandomData = true;
        const userIds = this.selectedUsers.map((user) => user._id);

        const result = await this.$store.dispatch('assessmentSchemas/generateRandomData', {
          schemaId: this.selectedRandomDataSchema,
          userIds,
        });

        this.showRandomDataDialog = false;
        this.selectedRandomDataSchema = null;
        this.randomDataChartType = null;

        this.$store.dispatch('notifications/showToast', {
          detail: result.message || `Successfully generated random data for ${userIds.length} user(s)`,
          severity: 'success',
        }, { root: true });
      } catch (error) {
        console.error('Failed to generate random data:', error);
      } finally {
        this.generatingRandomData = false;
      }
    },

    async executeGenerateKssTestData() {
      if (!this.kssTestDataName || !this.selectedUsers || this.selectedUsers.length === 0) return;

      try {
        this.generatingKssTestData = true;
        const payload = {
          userIds: this.selectedUsers.map((u) => u._id),
          name: this.kssTestDataName,
          occurrences: this.kssTestDataRecurring ? this.kssTestDataOccurrences : 1,
          frequency: this.kssTestDataRecurring ? this.kssTestDataFrequency : 'weekly',
          respondents: this.kssTestDataRespondents || this.selectedUsers.length,
        };

        const result = await this.$store.dispatch('generateKssTestData', payload);
        this.showKssTestDataDialog = false;

        const data = result?.data || result;
        this.$store.dispatch('notifications/showToast', {
          detail: `Created ${data.totalCreated} KSS assessments `
            + `(${data.submittedCount} submitted, ${data.openCount} open) `
            + `for series "${data.name}"`,
          severity: 'success',
        }, { root: true });
      } catch (error) {
        console.error('Failed to generate KSS test data:', error);
      } finally {
        this.generatingKssTestData = false;
      }
    },

    confirmRemoveKssTestData() {
      if (window.confirm('This will delete ALL generated KSS test data. Continue?')) {
        this.executeRemoveKssTestData();
      }
    },

    async executeRemoveKssTestData() {
      try {
        this.removingKssTestData = true;
        const result = await this.$store.dispatch('removeKssTestData');

        const data = result?.data || result;
        this.$store.dispatch('notifications/showToast', {
          detail: `Deleted ${data.deletedCount} KSS test assessment(s)`,
          severity: 'success',
        }, { root: true });
      } catch (error) {
        console.error('Failed to remove KSS test data:', error);
      } finally {
        this.removingKssTestData = false;
      }
    },

    async sendDevTipToUsers() {
      if (!this.selectedSchema || !this.selectedUsers || this.selectedUsers.length === 0) {
        return;
      }

      try {
        this.sendingDevTip = true;

        const userIds = this.selectedUsers.map((user) => user._id);
        // Use store action to avoid circular dependency
        const result = await this.$store.dispatch('assessmentSchemas/sendDevTipToSpecificUsers', {
          userIds,
          schemaId: this.selectedSchema,
        });

        // Store action handles success/error notifications
        // Show detailed results if there were errors
        if (result.data && result.data.errors && result.data.errors.length > 0) {
          const errorDetails = result.data.errors.map((err) => (
            err.userId ? `User ${err.userId}: ${err.error}` : err.error
          )).join('; ');

          this.$store.dispatch('notifications/showToast', {
            detail: `Some issues occurred: ${errorDetails}`,
            severity: 'warn',
          }, { root: true });
        }

        this.showDevTipDialog = false;
        this.selectedSchema = null;
      } catch (error) {
        console.error('Failed to send DevTips:', error);
        this.$store.dispatch('notifications/showToast', {
          detail: `Failed to send DevTips: ${error.message}`,
          severity: 'error',
        }, { root: true });
      } finally {
        this.sendingDevTip = false;
      }
    },
  },
  mounted() {
    this.init();
    this.updateMenuDisabledStates();
  },
};
</script>
